import { type FC, useMemo } from 'react';
import type { MessageAttachmentResponse } from '@/entities/conversations';
import { formatTextTypography } from '@/shared/lib';
import {
  collectContentAttachmentExternalIds,
  getUnplacedAttachments,
  groupAttachmentsByExternalId,
  splitTextByAttachments,
} from '../model/attachments';
import { parseContentHtml } from '../model/htmlContent';
import type { MessageContentSource } from '../model/messageText';
import { HtmlContent } from './HtmlContent';
import { MessageAttachmentView } from './MessageAttachmentView';

type Props = {
  /** Содержимое облака: оформленное либо простой текст */
  source: MessageContentSource;
  /** Вложения элемента переписки */
  attachments: MessageAttachmentResponse[];
};

/**
 * Содержимое облака: оформленное — оформленным, простой текст — с переносами строк. Вложение,
 * на которое содержимое ссылается, встаёт на место ссылки; остальные вложения — под содержимым.
 */
export const MessageContent: FC<Props> = ({ source, attachments }) => {
  const attachmentsByExternalId = useMemo(() => groupAttachmentsByExternalId(attachments), [attachments]);

  // Источник собирается заново при каждой отрисовке элемента — разбор держится за его значение
  const isHtml = source.kind === 'html';
  const sourceValue = source.kind === 'html' ? source.html : source.text;

  const content = useMemo(() => {
    if (isHtml) {
      const root = parseContentHtml(sourceValue);
      const placedExternalIds = collectContentAttachmentExternalIds(root, attachmentsByExternalId);
      return { kind: 'html', root, placedExternalIds } as const;
    }
    return { kind: 'text', ...splitTextByAttachments(sourceValue, attachmentsByExternalId) } as const;
  }, [isHtml, sourceValue, attachmentsByExternalId]);

  const restAttachments = useMemo(
    () => getUnplacedAttachments(attachments, content.placedExternalIds),
    [attachments, content.placedExternalIds],
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {content.kind === 'html' ? (
        <HtmlContent root={content.root} attachmentsByExternalId={attachmentsByExternalId} />
      ) : (
        <div className="min-w-0 whitespace-pre-wrap wrap-break-word">
          {content.segments.map((segment, index) =>
            segment.kind === 'text' ? (
              // Части текста не переставляются: индекс — устойчивый ключ
              <span key={index}>{formatTextTypography(segment.text)}</span>
            ) : (
              <span key={index} className="my-2 block whitespace-normal">
                <MessageAttachmentView attachment={segment.attachment} />
              </span>
            ),
          )}
        </div>
      )}
      {restAttachments.length > 0 && (
        <div className="flex flex-col gap-2">
          {restAttachments.map((attachment) => (
            <MessageAttachmentView key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
    </div>
  );
};
